import { type ReactNode } from 'react';
import { useRef, useState } from 'react';
import { type LatLng, type Map as LeafletMap } from 'leaflet';
import { Button, Icon, type ButtonProps } from '@ics-portal/guardian';
import PlainModal from '@components/_molecules/Modal/PlainModal';
import { Map } from '@components/_organisms/Map/map';
import {
    type MapControlContainerProps,
    MapControlPosition,
} from '@components/_organisms/Map/types';
import { MapControlType } from '@components/_organisms/Map/types';
import { BoxSwitch } from '@components/_atoms/BoxSwitch/box-switch';
import s from './project-location-editor.module.scss';
import { SelectedLocationController } from './SelectedLocationController';

interface ProjectLocationEditorProps {
    latLng?: LatLng;
    onChange?: (latLng: LatLng) => void;
    buttonProps?: ButtonProps;
}

export function ProjectLocationEditor({
    latLng,
    onChange,
    buttonProps,
}: ProjectLocationEditorProps): ReactNode {
    const [open, setOpen] = useState(false);
    const ref = useRef<LeafletMap>(null);
    const [selected, setSelected] = useState<LatLng>();

    const controls: MapControlContainerProps[] = [
        {
            position: MapControlPosition.BottomLeft,
            control: MapControlType.ZOOM,
        },
        // TODO: Implement search when api ready
        // {
        //     position: MapControlPosition.TOP_LEFT,
        //     control: onChange ? (
        //         <SearchController onChange={setSelected} />
        //     ) : null,
        // },
        {
            position: MapControlPosition.TopRight,
            control: (
                <BoxSwitch
                    buttons={[
                        {
                            icon: <Icon name="close" />,
                            onClick: () => {
                                setOpen(false);
                            },
                        },
                    ]}
                />
            ),
        },
        {
            position: MapControlPosition.BottomRight,
            control: onChange ? (
                <Button
                    label="Set location"
                    onClick={() => {
                        if (!selected) {
                            return;
                        }
                        onChange(selected);
                        setOpen(false);
                    }}
                />
            ) : (
                <></>
            ),
        },
    ];

    return (
        <>
            {Boolean(latLng) || onChange ? (
                <Button
                    className="mt-3"
                    severity="secondary"
                    label={onChange ? 'Edit location' : 'Show location'}
                    onClick={() => {
                        setOpen(true);
                        setSelected(latLng ?? undefined);
                        setTimeout(() => {
                            ref.current?.invalidateSize();
                        }, 1);
                    }}
                    {...(buttonProps ?? {})}
                />
            ) : null}
            <PlainModal open={open}>
                <div className={s.mapWrapper}>
                    <Map ref={ref} controls={controls}>
                        <SelectedLocationController
                            selected={selected}
                            disabled={!onChange}
                            onSelect={setSelected}
                        />
                    </Map>
                </div>
            </PlainModal>
        </>
    );
}
